import { createContext, FC, PropsWithChildren, useContext, useMemo } from 'react';
import { CombinedError } from 'urql';

import { useAllAppliedViewBy } from '@/hooks/discovery/allAppliedViewBy';
import { useEnvironmentScope } from '@/hooks/useEnvironmentScope';
import { useDbtCloudProject } from './DbtCloudProjectProvider';

type DatabaseName = string;
type SchemaName = string;
type DatabaseSet = Record<DatabaseName, Set<SchemaName>>;
type DatabaseMap = Record<DatabaseName, SchemaName[]>;

type DatabaseData = {
  databaseMap: DatabaseMap;
  isLoading: boolean;
  error: CombinedError | unknown | undefined;
};

const DatabaseContext = createContext<DatabaseData>({
  databaseMap: {},
  isLoading: false,
  error: undefined,
});

const sortDatabases = (databaseMap: DatabaseSet): DatabaseMap => {
  return Object.keys(databaseMap)
    .sort((a, b) => a.localeCompare(b))
    .reduce<DatabaseMap>((acc, database) => {
      acc[database] = Array.from(databaseMap[database]).sort((a, b) =>
        a.localeCompare(b),
      );
      return acc;
    }, {});
};

export const useDatabases = () => {
  return useContext(DatabaseContext);
};

export const DatabaseProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const { environmentId } = useDbtCloudProject();
  const { userId } = useEnvironmentScope();
  const [{ data, error, fetching }] = useAllAppliedViewBy({ environmentId, userId });

  const lineage = data?.environment.applied?.lineage;
  const hasError = !!error;
  const databaseMap = useMemo(() => {
    if (hasError || fetching) return {};
    const nodes = lineage ?? [];

    const databaseSet = nodes.reduce<DatabaseSet>((acc, { database, schema }) => {
      if (!database || !schema) return acc;
      database = database.toLowerCase();
      schema = schema.toLowerCase();
      acc[database] ??= new Set();
      acc[database].add(schema);
      return acc;
    }, {});

    return sortDatabases(databaseSet);
  }, [lineage, hasError, fetching]);

  return (
    <DatabaseContext.Provider
      value={{
        databaseMap: databaseMap,
        error: error,
        isLoading: fetching,
      }}
    >
      {children}
    </DatabaseContext.Provider>
  );
};
